import { useState, useEffect, useMemo, useCallback } from 'react';
import { usePark } from '../contexts/ParkContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  BarChart3,
  Download,
  FileText,
  FileSpreadsheet,
  Calendar,
  Filter,
  Users,
  Wallet,
  Receipt,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  generateIncomeExpenseReportPDF, 
  generateBudgetReportPDF, 
  generateAccountStatementPDF,
  generateCollectionReportPDF,
  generateBudgetDetailPDF,
  generateFeesByLotPDF,
  generateExpensesDetailPDF
} from '../utils/generateReportPDF';
import { 
  generateIncomeExpenseExcel, 
  generateBudgetExcel, 
  generateAccountStatementExcel,
  generateCollectionExcel,
  generateBudgetDetailExcel,
  generateFeesByLotExcel,
  generateExpensesDetailExcel
} from '../utils/generateReportExcel';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reportes = () => {
  const { parks, selectedPark, setSelectedPark, selectedYear } = usePark();
  const { canEditData, isConsulta, user } = useAuth();
  const [incomeReport, setIncomeReport] = useState(null);
  const [budgetReport, setBudgetReport] = useState(null);
  const [lots, setLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState('');
  const [accountStatement, setAccountStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allowedParks, setAllowedParks] = useState([]);
  const [loadingParks, setLoadingParks] = useState(true);
  
  // New reports state
  const [collectionReport, setCollectionReport] = useState(null);
  const [budgetDetail, setBudgetDetail] = useState(null);
  const [feesReport, setFeesReport] = useState(null);
  const [expensesDetail, setExpensesDetail] = useState(null);
  
  // Expenses filter state
  const [expCategoriaFilter, setExpCategoriaFilter] = useState('all');
  const [expSubcategoriaFilter, setExpSubcategoriaFilter] = useState('all');
  
  // Budget vs Expense tipo filter
  const [budgetTipoFilter, setBudgetTipoFilter] = useState('all');
  
  // Period filter state
  const [filterType, setFilterType] = useState('anual'); // 'anual', 'mensual', 'rango'
  const [mesInicio, setMesInicio] = useState(1);
  const [mesFin, setMesFin] = useState(12);

  const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const monthOptions = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];
  const COLORS = ['#0F172A', '#F59E0B', '#64748B', '#CBD5E1', '#94A3B8', '#475569'];

  // Sorting state for each report table
  const [sortConfig, setSortConfig] = useState({
    budgetExpense: { key: null, direction: 'asc' },
    collection: { key: null, direction: 'asc' },
    budgetDetail: { key: null, direction: 'asc' },
    feesLot: { key: null, direction: 'asc' },
    accountStatement: { key: null, direction: 'asc' },
    expensesDetail: { key: null, direction: 'asc' }
  });

  // Sortable Table Header Component
  const toggleSort = (tableKey, sortKey) => {
    setSortConfig(prev => ({
      ...prev,
      [tableKey]: {
        key: sortKey,
        direction: prev[tableKey]?.key === sortKey && prev[tableKey]?.direction === 'asc' ? 'desc' : 'asc'
      }
    }));
  };

  const SortableHeader = ({ label, sortKey, tableKey, align = 'left', className = '' }) => {
    const currentSort = sortConfig[tableKey];
    const isActive = currentSort?.key === sortKey;
    
    const handleSort = () => {
      setSortConfig(prev => ({
        ...prev,
        [tableKey]: {
          key: sortKey,
          direction: isActive && prev[tableKey].direction === 'asc' ? 'desc' : 'asc'
        }
      }));
    };

    return (
      <th 
        className={`cursor-pointer hover:bg-slate-100 select-none transition-colors ${align === 'right' ? 'text-right' : ''} ${className}`}
        onClick={handleSort}
      >
        <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
          <span>{label}</span>
          <span className="text-slate-400">
            {isActive ? (
              currentSort.direction === 'asc' ? (
                <ArrowUp size={14} className="text-amber-500" />
              ) : (
                <ArrowDown size={14} className="text-amber-500" />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </span>
        </div>
      </th>
    );
  };

  // Generic sorting function
  const sortData = (data, tableKey) => {
    const { key, direction } = sortConfig[tableKey];
    if (!key || !data) return data;

    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Handle numeric values
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle string values (case insensitive)
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      // Natural sort for alphanumeric strings (like "L-01", "L-02", "L-10")
      const aMatch = aStr.match(/^([a-z]*)(\d+)$/i);
      const bMatch = bStr.match(/^([a-z]*)(\d+)$/i);
      
      if (aMatch && bMatch && aMatch[1] === bMatch[1]) {
        const aNum = parseInt(aMatch[2], 10);
        const bNum = parseInt(bMatch[2], 10);
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Standard string comparison
      if (aStr < bStr) return direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Memoized sorted data for each report
  const sortedBudgetExpenseData = useMemo(() => 
    sortData(budgetReport?.data, 'budgetExpense'),
    [budgetReport?.data, sortConfig.budgetExpense]
  );

  // Filtered budget-expense data by tipo_cuota
  const filteredBudgetExpenseData = useMemo(() => {
    if (!sortedBudgetExpenseData) return [];
    if (budgetTipoFilter === 'all') return sortedBudgetExpenseData;
    return sortedBudgetExpenseData.filter(row => (row.tipo_cuota || 'ordinario') === budgetTipoFilter);
  }, [sortedBudgetExpenseData, budgetTipoFilter]);

  const filteredBudgetTotals = useMemo(() => {
    const presupuesto = filteredBudgetExpenseData.reduce((s, r) => s + (r.presupuesto_anual || 0), 0);
    const gasto_real = filteredBudgetExpenseData.reduce((s, r) => s + (r.gasto_real || 0), 0);
    const porcentaje = presupuesto > 0 ? (gasto_real / presupuesto * 100) : 0;
    return {
      presupuesto: Math.round(presupuesto * 100) / 100,
      gasto_real: Math.round(gasto_real * 100) / 100,
      porcentaje: Math.round(porcentaje * 100) / 100,
      diferencia: Math.round((presupuesto - gasto_real) * 100) / 100
    };
  }, [filteredBudgetExpenseData]);

  const sortedCollectionData = useMemo(() => 
    sortData(collectionReport?.data, 'collection'),
    [collectionReport?.data, sortConfig.collection]
  );

  const sortedBudgetDetailData = useMemo(() => 
    sortData(budgetDetail?.data, 'budgetDetail'),
    [budgetDetail?.data, sortConfig.budgetDetail]
  );

  const sortedFeesData = useMemo(() => 
    sortData(feesReport?.data, 'feesLot'),
    [feesReport?.data, sortConfig.feesLot]
  );

  const sortedAccountMovements = useMemo(() => 
    sortData(accountStatement?.movements, 'accountStatement'),
    [accountStatement?.movements, sortConfig.accountStatement]
  );

  const sortedExpensesData = useMemo(() => 
    sortData(expensesDetail?.data, 'expensesDetail'),
    [expensesDetail?.data, sortConfig.expensesDetail]
  );

  // Fetch allowed parks for consultant users
  useEffect(() => {
    const fetchAllowedParks = async () => {
      if (canEditData()) {
        // Admins can see all parks
        setAllowedParks(parks);
        setLoadingParks(false);
        return;
      }

      // For consultants, fetch only parks where they have assigned lots
      try {
        setLoadingParks(true);
        // Get lot assignments for this user
        const lotUsersRes = await axios.get(`${API}/lot-users?user_id=${user.id}`);
        const userLotIds = lotUsersRes.data.map(lu => lu.lot_id);

        if (userLotIds.length === 0) {
          setAllowedParks([]);
          setLoadingParks(false);
          return;
        }

        // Get lot details to find park_ids
        const lotsRes = await axios.get(`${API}/lots`);
        const userLots = lotsRes.data.filter(lot => userLotIds.includes(lot.id));
        
        // Get unique park IDs from user's lots
        const userParkIds = [...new Set(userLots.map(lot => lot.park_id))];
        
        // Filter parks to only those the user has access to
        const filteredParks = parks.filter(park => userParkIds.includes(park.id));
        setAllowedParks(filteredParks);

        // If current selected park is not in allowed parks, select first allowed
        if (filteredParks.length > 0 && !filteredParks.find(p => p.id === selectedPark?.id)) {
          setSelectedPark(filteredParks[0]);
        }
      } catch (error) {
        console.error('Error fetching allowed parks:', error);
        setAllowedParks([]);
      } finally {
        setLoadingParks(false);
      }
    };

    if (user && parks.length > 0) {
      fetchAllowedParks();
    }
  }, [user, parks, canEditData]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      // Build query params based on filter type
      let queryParams = `park_id=${selectedPark.id}&año=${selectedYear}`;
      if (filterType === 'mensual') {
        queryParams += `&mes_inicio=${mesInicio}&mes_fin=${mesInicio}`;
      } else if (filterType === 'rango') {
        queryParams += `&mes_inicio=${mesInicio}&mes_fin=${mesFin}`;
      }
      
      const [incomeRes, budgetRes, lotsRes, collectionRes, budgetDetailRes, feesRes, expensesRes] = await Promise.all([
        axios.get(`${API}/reports/income-vs-expenses?${queryParams}`),
        axios.get(`${API}/reports/budget-vs-expenses?${queryParams}`),
        axios.get(`${API}/lots?park_id=${selectedPark.id}`),
        axios.get(`${API}/reports/collection?${queryParams}`),
        axios.get(`${API}/reports/budget-detail?park_id=${selectedPark.id}&año=${selectedYear}`),
        axios.get(`${API}/reports/fees-by-lot?${queryParams}`),
        axios.get(`${API}/reports/expenses-detail?${queryParams}`)
      ]);
      
      setIncomeReport(incomeRes.data);
      setBudgetReport(budgetRes.data);
      setLots(lotsRes.data);
      setCollectionReport(collectionRes.data);
      setBudgetDetail(budgetDetailRes.data);
      setFeesReport(feesRes.data);
      setExpensesDetail(expensesRes.data);
      setExpCategoriaFilter('all');
      setExpSubcategoriaFilter('all');
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedPark, selectedYear, filterType, mesInicio, mesFin]);

  const fetchAccountStatement = useCallback(async () => {
    try {
      let queryParams = `lot_id=${selectedLot}&año=${selectedYear}`;
      if (filterType === 'mensual') {
        queryParams += `&mes_inicio=${mesInicio}&mes_fin=${mesInicio}`;
      } else if (filterType === 'rango') {
        queryParams += `&mes_inicio=${mesInicio}&mes_fin=${mesFin}`;
      }
      const response = await axios.get(`${API}/reports/account-statement?${queryParams}`);
      setAccountStatement(response.data);
    } catch (error) {
      console.error('Error fetching account statement:', error);
    }
  }, [selectedLot, selectedYear, filterType, mesInicio, mesFin]);

  useEffect(() => {
    if (selectedPark && !loadingParks) {
      // Only fetch reports if the user has access to this park
      const hasAccess = canEditData() || allowedParks.find(p => p.id === selectedPark.id);
      if (hasAccess) {
        fetchReports();
      }
    }
  }, [selectedPark, loadingParks, allowedParks, fetchReports]);

  useEffect(() => {
    if (selectedLot) {
      fetchAccountStatement();
    }
  }, [selectedLot, fetchAccountStatement]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Get period label for display
  const getPeriodLabel = () => {
    if (filterType === 'anual') return `Año ${selectedYear}`;
    if (filterType === 'mensual') return `${monthOptions[mesInicio - 1]?.label} ${selectedYear}`;
    return `${monthOptions[mesInicio - 1]?.label} - ${monthOptions[mesFin - 1]?.label} ${selectedYear}`;
  };

  const handleExportIncomePDF = () => {
    if (!incomeReport) return;
    generateIncomeExpenseReportPDF(selectedPark.nombre, selectedYear, incomeReport, getPeriodLabel());
    toast.success('PDF generado exitosamente');
  };

  const handleExportIncomeExcel = () => {
    if (!incomeReport) return;
    generateIncomeExpenseExcel(selectedPark.nombre, selectedYear, incomeReport, getPeriodLabel());
    toast.success('Excel generado exitosamente');
  };

  const handleExportBudgetPDF = (tipoFilter = 'all') => {
    if (!budgetReport) return;
    let dataToExport = sortedBudgetExpenseData;
    if (tipoFilter !== 'all') {
      dataToExport = sortedBudgetExpenseData.filter(r => (r.tipo_cuota || 'ordinario') === tipoFilter);
    }
    const presupuesto = dataToExport.reduce((s, r) => s + (r.presupuesto_anual || 0), 0);
    const gasto_real = dataToExport.reduce((s, r) => s + (r.gasto_real || 0), 0);
    const sortedReport = {
      data: dataToExport,
      totals: {
        presupuesto: Math.round(presupuesto * 100) / 100,
        gasto_real: Math.round(gasto_real * 100) / 100,
        porcentaje: presupuesto > 0 ? Math.round((gasto_real / presupuesto * 100) * 100) / 100 : 0,
        diferencia: Math.round((presupuesto - gasto_real) * 100) / 100
      }
    };
    const suffix = tipoFilter === 'ordinario' ? ' (Ordinario)' : tipoFilter === 'extraordinario' ? ' (Extraordinario)' : '';
    generateBudgetReportPDF(selectedPark.nombre + suffix, selectedYear, sortedReport, getPeriodLabel());
    toast.success('PDF generado exitosamente');
  };

  const handleExportBudgetExcel = (tipoFilter = 'all') => {
    if (!budgetReport) return;
    let dataToExport = sortedBudgetExpenseData;
    if (tipoFilter !== 'all') {
      dataToExport = sortedBudgetExpenseData.filter(r => (r.tipo_cuota || 'ordinario') === tipoFilter);
    }
    const presupuesto = dataToExport.reduce((s, r) => s + (r.presupuesto_anual || 0), 0);
    const gasto_real = dataToExport.reduce((s, r) => s + (r.gasto_real || 0), 0);
    const sortedReport = {
      data: dataToExport,
      totals: {
        presupuesto: Math.round(presupuesto * 100) / 100,
        gasto_real: Math.round(gasto_real * 100) / 100,
        porcentaje: presupuesto > 0 ? Math.round((gasto_real / presupuesto * 100) * 100) / 100 : 0,
        diferencia: Math.round((presupuesto - gasto_real) * 100) / 100
      }
    };
    const suffix = tipoFilter === 'ordinario' ? ' (Ordinario)' : tipoFilter === 'extraordinario' ? ' (Extraordinario)' : '';
    generateBudgetExcel(selectedPark.nombre + suffix, selectedYear, sortedReport, getPeriodLabel());
    toast.success('Excel generado exitosamente');
  };

  const handleExportAccountPDF = () => {
    if (!accountStatement) return;
    // Use sorted data for export
    const sortedStatement = {
      ...accountStatement,
      movements: sortedAccountMovements
    };
    generateAccountStatementPDF(selectedPark.nombre, selectedYear, sortedStatement);
    toast.success('PDF generado exitosamente');
  };

  const handleExportAccountExcel = () => {
    if (!accountStatement) return;
    // Use sorted data for export
    const sortedStatement = {
      ...accountStatement,
      movements: sortedAccountMovements
    };
    generateAccountStatementExcel(selectedPark.nombre, selectedYear, sortedStatement);
    toast.success('Excel generado exitosamente');
  };

  // New report export handlers
  const handleExportCollectionPDF = () => {
    if (!collectionReport) return;
    // Use sorted data for export
    const sortedReport = {
      ...collectionReport,
      data: sortedCollectionData
    };
    generateCollectionReportPDF(selectedPark.nombre, selectedYear, sortedReport, getPeriodLabel());
    toast.success('PDF generado exitosamente');
  };

  const handleExportCollectionExcel = () => {
    if (!collectionReport) return;
    // Use sorted data for export
    const sortedReport = {
      ...collectionReport,
      data: sortedCollectionData
    };
    generateCollectionExcel(selectedPark.nombre, selectedYear, sortedReport, getPeriodLabel());
    toast.success('Excel generado exitosamente');
  };

  const handleExportBudgetDetailPDF = () => {
    if (!budgetDetail) return;
    // Use sorted data for export
    const sortedDetail = {
      ...budgetDetail,
      data: sortedBudgetDetailData
    };
    generateBudgetDetailPDF(selectedPark.nombre, selectedYear, sortedDetail);
    toast.success('PDF generado exitosamente');
  };

  const handleExportBudgetDetailExcel = () => {
    if (!budgetDetail) return;
    // Use sorted data for export
    const sortedDetail = {
      ...budgetDetail,
      data: sortedBudgetDetailData
    };
    generateBudgetDetailExcel(selectedPark.nombre, selectedYear, sortedDetail);
    toast.success('Excel generado exitosamente');
  };

  const handleExportFeesPDF = () => {
    if (!feesReport) return;
    // Use sorted data for export
    const sortedReport = {
      ...feesReport,
      data: sortedFeesData
    };
    generateFeesByLotPDF(selectedPark.nombre, selectedYear, sortedReport, getPeriodLabel());
    toast.success('PDF generado exitosamente');
  };

  const handleExportFeesExcel = () => {
    if (!feesReport) return;
    // Use sorted data for export
    const sortedReport = {
      ...feesReport,
      data: sortedFeesData
    };
    generateFeesByLotExcel(selectedPark.nombre, selectedYear, sortedReport, getPeriodLabel());
    toast.success('Excel generado exitosamente');
  };

  // Expenses detail - filtered data
  const filteredExpensesData = useMemo(() => {
    if (!expensesDetail?.data) return [];
    return expensesDetail.data.filter(row => {
      if (expCategoriaFilter !== 'all' && row.categoria !== expCategoriaFilter) return false;
      if (expSubcategoriaFilter !== 'all' && row.subcategoria !== expSubcategoriaFilter) return false;
      return true;
    });
  }, [expensesDetail?.data, expCategoriaFilter, expSubcategoriaFilter]);

  const filteredExpensesTotals = useMemo(() => {
    const total = filteredExpensesData.reduce((sum, r) => sum + (r.monto || 0), 0);
    return { total_gastos: Math.round(total * 100) / 100, total_registros: filteredExpensesData.length };
  }, [filteredExpensesData]);

  const sortedExpensesFiltered = useMemo(() => 
    sortData(filteredExpensesData, 'expensesDetail'),
    [filteredExpensesData, sortConfig.expensesDetail]
  );

  // Subcategorias filtered by selected category
  const expSubcategoriasFiltered = useMemo(() => {
    if (!expensesDetail?.data) return [];
    const filtered = expCategoriaFilter === 'all' 
      ? expensesDetail.data 
      : expensesDetail.data.filter(r => r.categoria === expCategoriaFilter);
    return [...new Set(filtered.map(r => r.subcategoria).filter(Boolean))].sort();
  }, [expensesDetail?.data, expCategoriaFilter]);

  const getExpActiveFiltersLabel = () => {
    const parts = [];
    if (expCategoriaFilter !== 'all') parts.push(`Categoría: ${expCategoriaFilter}`);
    if (expSubcategoriaFilter !== 'all') parts.push(`Subcategoría: ${expSubcategoriaFilter}`);
    return parts.join(' | ');
  };

  const handleExportExpensesPDF = () => {
    if (!expensesDetail) return;
    const exportReport = { data: sortedExpensesFiltered, totals: filteredExpensesTotals };
    generateExpensesDetailPDF(selectedPark.nombre, selectedYear, exportReport, getPeriodLabel(), getExpActiveFiltersLabel());
    toast.success('PDF generado exitosamente');
  };

  const handleExportExpensesExcel = () => {
    if (!expensesDetail) return;
    const exportReport = { data: sortedExpensesFiltered, totals: filteredExpensesTotals };
    generateExpensesDetailExcel(selectedPark.nombre, selectedYear, exportReport, getPeriodLabel(), getExpActiveFiltersLabel());
    toast.success('Excel generado exitosamente');
  };

  const incomeChartData = incomeReport?.data?.map((item) => ({
    mes: monthNames[item.mes - 1],
    Ingresos: item.ingresos_cobrados,
    Gastos: item.gastos,
    Resultado: item.resultado
  })) || [];

  const budgetChartData = budgetReport?.data?.map(item => ({
    name: item.categoria,
    Presupuesto: item.presupuesto_anual,
    Gastado: item.gasto_real
  })) || [];

  const pieData = budgetReport?.data?.map(item => ({
    name: item.categoria,
    value: item.gasto_real
  })).filter(item => item.value > 0) || [];

  // Display parks to use (for consultant dropdown)
  const displayParks = canEditData() ? parks : allowedParks;

  if (loadingParks) {
    return (
      <div className="animate-fadeIn">
        <div className="page-header">
          <h1>Reportes</h1>
          <p>Cargando parques...</p>
        </div>
        <div className="card-industrial animate-pulse h-48"></div>
      </div>
    );
  }

  // For consultants with no assigned parks
  if (!canEditData() && allowedParks.length === 0) {
    return (
      <div className="animate-fadeIn">
        <div className="page-header">
          <h1>Reportes</h1>
          <p>No tienes parques asignados</p>
        </div>
        <div className="empty-state py-16 card-industrial">
          <div className="empty-state-icon">
            <BarChart3 size={24} className="text-slate-400" />
          </div>
          <h3>Sin acceso a reportes</h3>
          <p>No tienes ningún lote asignado. Contacta al administrador para obtener acceso.</p>
        </div>
      </div>
    );
  }

  if (!selectedPark) {
    return (
      <div className="animate-fadeIn">
        <div className="page-header">
          <h1>Reportes</h1>
          <p>Selecciona un parque para ver los reportes</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div className="page-header">
        <h1 data-testid="reportes-title">Reportes</h1>
        <p>{selectedPark?.nombre} - {getPeriodLabel()}</p>
      </div>

      {/* Park selector for consultants (they don't have the global selector) */}
      {!canEditData() && displayParks.length > 1 && (
        <div className="card-industrial mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="label-overline block mb-2">Seleccionar Parque</label>
              <Select 
                value={selectedPark?.id} 
                onValueChange={(value) => {
                  const park = displayParks.find(p => p.id === value);
                  if (park) setSelectedPark(park);
                }}
              >
                <SelectTrigger className="w-64 input-industrial" data-testid="park-select-consultant">
                  <SelectValue placeholder="Seleccionar parque..." />
                </SelectTrigger>
                <SelectContent>
                  {displayParks.map(park => (
                    <SelectItem key={park.id} value={park.id}>{park.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Period Filter */}
      <div className="card-industrial mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar size={18} className="text-slate-500" />
          <span className="font-medium text-slate-700">Filtrar por Período</span>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="label-overline block mb-2">Tipo de Período</label>
            <Select 
              value={filterType} 
              onValueChange={(v) => {
                setFilterType(v);
                if (v === 'anual') {
                  setMesInicio(1);
                  setMesFin(12);
                } else if (v === 'mensual') {
                  setMesFin(mesInicio);
                }
              }}
            >
              <SelectTrigger className="w-44 input-industrial" data-testid="filter-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="anual">Año Completo</SelectItem>
                <SelectItem value="mensual">Mes Específico</SelectItem>
                <SelectItem value="rango">Rango de Meses</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {filterType === 'mensual' && (
            <div>
              <label className="label-overline block mb-2">Mes</label>
              <Select 
                value={mesInicio.toString()} 
                onValueChange={(v) => {
                  const m = parseInt(v);
                  setMesInicio(m);
                  setMesFin(m);
                }}
              >
                <SelectTrigger className="w-40 input-industrial" data-testid="filter-mes-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {filterType === 'rango' && (
            <>
              <div>
                <label className="label-overline block mb-2">Desde</label>
                <Select 
                  value={mesInicio.toString()} 
                  onValueChange={(v) => {
                    const m = parseInt(v);
                    setMesInicio(m);
                    if (m > mesFin) setMesFin(m);
                  }}
                >
                  <SelectTrigger className="w-40 input-industrial" data-testid="filter-mes-inicio">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map(m => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-overline block mb-2">Hasta</label>
                <Select 
                  value={mesFin.toString()} 
                  onValueChange={(v) => setMesFin(parseInt(v))}
                >
                  <SelectTrigger className="w-40 input-industrial" data-testid="filter-mes-fin">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.filter(m => m.value >= mesInicio).map(m => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          
          <div className="flex items-center text-sm text-slate-500 bg-slate-50 px-3 py-2 border border-slate-200">
            <Filter size={14} className="mr-2" />
            {filterType === 'anual' && `Mostrando: Año ${selectedYear} completo`}
            {filterType === 'mensual' && `Mostrando: ${monthOptions[mesInicio - 1]?.label} ${selectedYear}`}
            {filterType === 'rango' && `Mostrando: ${monthOptions[mesInicio - 1]?.label} - ${monthOptions[mesFin - 1]?.label} ${selectedYear}`}
          </div>
        </div>
      </div>

      <Tabs defaultValue="ingresos-gastos" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-none flex-wrap">
          <TabsTrigger value="ingresos-gastos" className="rounded-none data-[state=active]:bg-white" data-testid="tab-ingresos-gastos">
            Ingresos vs Gastos
          </TabsTrigger>
          <TabsTrigger value="presupuesto" className="rounded-none data-[state=active]:bg-white" data-testid="tab-presupuesto">
            Gasto vs Presupuesto
          </TabsTrigger>
          {canEditData() && (
            <>
              <TabsTrigger value="cobranza" className="rounded-none data-[state=active]:bg-white" data-testid="tab-cobranza">
                Cobranza
              </TabsTrigger>
              <TabsTrigger value="presupuesto-detalle" className="rounded-none data-[state=active]:bg-white" data-testid="tab-presupuesto-detalle">
                Presupuesto
              </TabsTrigger>
              <TabsTrigger value="cuotas-lote" className="rounded-none data-[state=active]:bg-white" data-testid="tab-cuotas-lote">
                Cuotas por Lote
              </TabsTrigger>
              <TabsTrigger value="gastos-detalle" className="rounded-none data-[state=active]:bg-white" data-testid="tab-gastos-detalle">
                Gastos
              </TabsTrigger>
              <TabsTrigger value="estado-cuenta" className="rounded-none data-[state=active]:bg-white" data-testid="tab-estado-cuenta">
                Estado de Cuenta
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Ingresos vs Gastos */}
        <TabsContent value="ingresos-gastos">
          {loading ? (
            <div className="card-industrial animate-pulse h-96"></div>
          ) : (
            <>
              {/* Export buttons */}
              <div className="flex justify-end gap-2 mb-4">
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={handleExportIncomePDF}
                  disabled={!incomeReport}
                  data-testid="export-income-pdf"
                >
                  <Download size={16} className="mr-2" />
                  Descargar PDF
                </Button>
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={handleExportIncomeExcel}
                  disabled={!incomeReport}
                  data-testid="export-income-excel"
                >
                  <FileSpreadsheet size={16} className="mr-2" />
                  Descargar Excel
                </Button>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="card-industrial">
                  <span className="label-overline">Cuotas Generadas</span>
                  <p className="text-2xl font-black tracking-tight mt-2 tabular-nums">
                    {formatCurrency(incomeReport?.totals?.cuotas_generadas)}
                  </p>
                </div>
                <div className="card-industrial">
                  <span className="label-overline">Ingresos Cobrados</span>
                  <p className="text-2xl font-black tracking-tight mt-2 tabular-nums text-emerald-600">
                    {formatCurrency(incomeReport?.totals?.ingresos_cobrados)}
                  </p>
                </div>
                <div className="card-industrial">
                  <span className="label-overline">Gastos Totales</span>
                  <p className="text-2xl font-black tracking-tight mt-2 tabular-nums text-red-600">
                    {formatCurrency(incomeReport?.totals?.gastos)}
                  </p>
                </div>
                <div className="card-industrial">
                  <span className="label-overline">Resultado Neto</span>
                  <p className={`text-2xl font-black tracking-tight mt-2 tabular-nums ${incomeReport?.totals?.resultado >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(incomeReport?.totals?.resultado)}
                  </p>
                </div>
              </div>

              {/* Chart */}
              <div className="card-industrial">
                <h3 className="font-bold text-lg mb-6">
                  {filterType === 'mensual' 
                    ? `Detalle de ${monthOptions[mesInicio - 1]?.label}` 
                    : 'Comparativa Mensual'}
                </h3>
                {incomeChartData.length > 0 ? (
                  <div className="h-80" data-testid="income-expense-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={incomeChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="mes" tick={{ fill: '#64748B', fontSize: 12 }} />
                        <YAxis tick={{ fill: '#64748B', fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="Ingresos" fill="#0F172A" />
                        <Bar dataKey="Gastos" fill="#F59E0B" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">No hay datos disponibles</div>
                )}
              </div>

              {/* Table */}
              <div className="card-industrial overflow-hidden p-0 mt-6">
                <div className="overflow-x-auto">
                  <table className="table-blueprint" data-testid="income-expense-table">
                    <thead>
                      <tr>
                        <th>Mes</th>
                        <th className="text-right">Cuotas Generadas</th>
                        <th className="text-right">Ingresos Cobrados</th>
                        <th className="text-right">Gastos</th>
                        <th className="text-right">Resultado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {incomeReport?.data?.map((row, index) => (
                        <tr key={index}>
                          <td className="font-medium">{monthNames[row.mes - 1]}</td>
                          <td className="text-right tabular-nums">{formatCurrency(row.cuotas_generadas)}</td>
                          <td className="text-right tabular-nums text-emerald-600">{formatCurrency(row.ingresos_cobrados)}</td>
                          <td className="text-right tabular-nums text-red-600">{formatCurrency(row.gastos)}</td>
                          <td className={`text-right tabular-nums font-medium ${row.resultado >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatCurrency(row.resultado)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-50 font-bold">
                        <td>{filterType === 'anual' ? 'Total Anual' : 'Total Período'}</td>
                        <td className="text-right tabular-nums">{formatCurrency(incomeReport?.totals?.cuotas_generadas)}</td>
                        <td className="text-right tabular-nums text-emerald-600">{formatCurrency(incomeReport?.totals?.ingresos_cobrados)}</td>
                        <td className="text-right tabular-nums text-red-600">{formatCurrency(incomeReport?.totals?.gastos)}</td>
                        <td className={`text-right tabular-nums ${incomeReport?.totals?.resultado >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(incomeReport?.totals?.resultado)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Gasto vs Presupuesto */}
        <TabsContent value="presupuesto">
          {loading ? (
            <div className="card-industrial animate-pulse h-96"></div>
          ) : (
            <>
              {/* Filter and Export buttons */}
              <div className="card-industrial mb-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <label className="label-overline block mb-2">Tipo de Presupuesto</label>
                    <Select value={budgetTipoFilter} onValueChange={setBudgetTipoFilter}>
                      <SelectTrigger className="w-52 input-industrial" data-testid="budget-tipo-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Mixto (Todos)</SelectItem>
                        <SelectItem value="ordinario">Solo Ordinario</SelectItem>
                        <SelectItem value="extraordinario">Solo Extraordinario</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 ml-auto flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => handleExportBudgetPDF('all')} disabled={!budgetReport} data-testid="export-budget-pdf-mixed">
                      <Download size={14} className="mr-1" /> PDF Mixto
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExportBudgetPDF('ordinario')} disabled={!budgetReport} data-testid="export-budget-pdf-ord">
                      <Download size={14} className="mr-1" /> PDF Ordinario
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExportBudgetPDF('extraordinario')} disabled={!budgetReport} data-testid="export-budget-pdf-extra">
                      <Download size={14} className="mr-1" /> PDF Extra
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExportBudgetExcel('all')} disabled={!budgetReport} data-testid="export-budget-excel-mixed">
                      <FileSpreadsheet size={14} className="mr-1" /> Excel Mixto
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExportBudgetExcel('ordinario')} disabled={!budgetReport} data-testid="export-budget-excel-ord">
                      <FileSpreadsheet size={14} className="mr-1" /> Excel Ordinario
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExportBudgetExcel('extraordinario')} disabled={!budgetReport} data-testid="export-budget-excel-extra">
                      <FileSpreadsheet size={14} className="mr-1" /> Excel Extra
                    </Button>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="card-industrial">
                  <span className="label-overline">Presupuesto {budgetTipoFilter === 'all' ? 'Total' : budgetTipoFilter === 'ordinario' ? 'Ordinario' : 'Extraordinario'}</span>
                  <p className="text-2xl font-black tracking-tight mt-2 tabular-nums">
                    {formatCurrency(filteredBudgetTotals.presupuesto)}
                  </p>
                </div>
                <div className="card-industrial">
                  <span className="label-overline">Gasto Real</span>
                  <p className="text-2xl font-black tracking-tight mt-2 tabular-nums text-red-600">
                    {formatCurrency(filteredBudgetTotals.gasto_real)}
                  </p>
                </div>
                <div className="card-industrial">
                  <span className="label-overline">% Ejecutado</span>
                  <p className={`text-2xl font-black tracking-tight mt-2 tabular-nums ${filteredBudgetTotals.porcentaje > 100 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {filteredBudgetTotals.porcentaje?.toFixed(1)}%
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Disponible: {formatCurrency(filteredBudgetTotals.diferencia)}
                  </p>
                </div>
              </div>

              {/* Table */}
              <div className="card-industrial overflow-hidden p-0 mt-6">
                <div className="overflow-x-auto">
                  <table className="table-blueprint" data-testid="budget-expense-table">
                    <thead>
                      <tr>
                        <SortableHeader label="Categoría" sortKey="categoria" tableKey="budgetExpense" />
                        <SortableHeader label="Subcategoría" sortKey="subcategoria" tableKey="budgetExpense" />
                        <SortableHeader label="Tipo" sortKey="tipo_cuota" tableKey="budgetExpense" />
                        <SortableHeader label="Presupuesto Anual" sortKey="presupuesto_anual" tableKey="budgetExpense" align="right" />
                        <SortableHeader label="Gasto Real" sortKey="gasto_real" tableKey="budgetExpense" align="right" />
                        <SortableHeader label="% Ejecutado" sortKey="porcentaje_ejecutado" tableKey="budgetExpense" align="right" />
                        <SortableHeader label="Diferencia" sortKey="diferencia" tableKey="budgetExpense" align="right" />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBudgetExpenseData?.map((row, index) => (
                        <tr key={`${row.categoria}-${row.subcategoria}-${row.tipo_cuota}-${index}`}>
                          <td className="font-medium">{row.categoria}</td>
                          <td className="text-slate-500">{row.subcategoria || '-'}</td>
                          <td>
                            <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                              row.tipo_cuota === 'extraordinario' 
                                ? 'bg-amber-100 text-amber-800' 
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {row.tipo_cuota === 'extraordinario' ? 'Extra' : 'Ord.'}
                            </span>
                          </td>
                          <td className="text-right tabular-nums">{formatCurrency(row.presupuesto_anual)}</td>
                          <td className="text-right tabular-nums text-red-600">{formatCurrency(row.gasto_real)}</td>
                          <td className="text-right tabular-nums">
                            <span className={`px-2 py-1 text-xs font-medium ${row.porcentaje_ejecutado > 100 ? 'bg-red-100 text-red-800' : row.porcentaje_ejecutado > 80 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                              {row.porcentaje_ejecutado.toFixed(1)}%
                            </span>
                          </td>
                          <td className={`text-right tabular-nums font-medium ${row.diferencia >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatCurrency(row.diferencia)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-50 font-bold">
                        <td colSpan={3}>Total</td>
                        <td className="text-right tabular-nums">{formatCurrency(filteredBudgetTotals.presupuesto)}</td>
                        <td className="text-right tabular-nums text-red-600">{formatCurrency(filteredBudgetTotals.gasto_real)}</td>
                        <td className="text-right tabular-nums">{filteredBudgetTotals.porcentaje?.toFixed(1)}%</td>
                        <td className={`text-right tabular-nums ${filteredBudgetTotals.diferencia >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(filteredBudgetTotals.diferencia)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Reporte de Cobranza */}
        <TabsContent value="cobranza">
          {loading ? (
            <div className="card-industrial animate-pulse h-96"></div>
          ) : (
            <>
              {/* Export buttons */}
              <div className="flex justify-end gap-2 mb-4">
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={handleExportCollectionPDF}
                  disabled={!collectionReport}
                >
                  <Download size={16} className="mr-2" />
                  Descargar PDF
                </Button>
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={handleExportCollectionExcel}
                  disabled={!collectionReport}
                >
                  <FileSpreadsheet size={16} className="mr-2" />
                  Descargar Excel
                </Button>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="card-industrial text-center">
                  <p className="label-overline">Total Lotes</p>
                  <p className="text-3xl font-bold">{collectionReport?.summary?.total_lotes || 0}</p>
                </div>
                <div className="card-industrial text-center">
                  <p className="label-overline">Al Corriente</p>
                  <p className="text-3xl font-bold text-emerald-600">{collectionReport?.summary?.lotes_al_corriente || 0}</p>
                </div>
                <div className="card-industrial text-center">
                  <p className="label-overline">Con Adeudo</p>
                  <p className="text-3xl font-bold text-red-600">{collectionReport?.summary?.lotes_con_adeudo || 0}</p>
                </div>
                <div className="card-industrial text-center">
                  <p className="label-overline">% Cobranza</p>
                  <p className="text-3xl font-bold text-amber-500">{collectionReport?.summary?.porcentaje_cobranza || 0}%</p>
                </div>
              </div>

              {/* Totals */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="card-industrial bg-slate-50">
                  <p className="label-overline mb-2">Total Cuotas (Período)</p>
                  <p className="text-xl font-bold">{formatCurrency(collectionReport?.totals?.total_cuotas || 0)}</p>
                  <p className="text-sm text-slate-500">Base: {formatCurrency(collectionReport?.totals?.total_cuotas_base || 0)} + IVA: {formatCurrency(collectionReport?.totals?.total_cuotas_iva || 0)}</p>
                </div>
                <div className="card-industrial bg-emerald-50">
                  <p className="label-overline mb-2">Total Pagado</p>
                  <p className="text-xl font-bold text-emerald-600">{formatCurrency(collectionReport?.totals?.total_pagado || 0)}</p>
                  <p className="text-sm text-slate-500">Base: {formatCurrency(collectionReport?.totals?.total_pagado_base || 0)} + IVA: {formatCurrency(collectionReport?.totals?.total_pagado_iva || 0)}</p>
                </div>
                <div className="card-industrial bg-red-50">
                  <p className="label-overline mb-2">Total Adeudo</p>
                  <p className="text-xl font-bold text-red-600">{formatCurrency(collectionReport?.totals?.total_saldo || 0)}</p>
                  <p className="text-sm text-slate-500">Base: {formatCurrency(collectionReport?.totals?.total_saldo_base || 0)} + IVA: {formatCurrency(collectionReport?.totals?.total_saldo_iva || 0)}</p>
                </div>
              </div>

              {/* Table */}
              <div className="card-industrial overflow-hidden p-0">
                <div className="overflow-x-auto">
                  <table className="table-blueprint">
                    <thead>
                      <tr>
                        <SortableHeader label="Lote" sortKey="numero_lote" tableKey="collection" />
                        <SortableHeader label="Propietario" sortKey="propietario" tableKey="collection" />
                        <SortableHeader label="Cuotas" sortKey="cuotas_base" tableKey="collection" align="right" />
                        <SortableHeader label="Pagado" sortKey="pagado_base" tableKey="collection" align="right" />
                        <SortableHeader label="Saldo Base" sortKey="saldo_base" tableKey="collection" align="right" />
                        <SortableHeader label="Saldo IVA" sortKey="saldo_iva" tableKey="collection" align="right" />
                        <SortableHeader label="Saldo Total" sortKey="saldo_total" tableKey="collection" align="right" />
                        <SortableHeader label="Estado" sortKey="estado" tableKey="collection" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCollectionData?.map((row, index) => (
                        <tr key={index}>
                          <td className="font-mono font-medium">{row.numero_lote}</td>
                          <td>{row.propietario || '-'}</td>
                          <td className="text-right tabular-nums">{formatCurrency(row.cuotas_base)}</td>
                          <td className="text-right tabular-nums text-emerald-600">{formatCurrency(row.pagado_base)}</td>
                          <td className="text-right tabular-nums">{formatCurrency(row.saldo_base)}</td>
                          <td className="text-right tabular-nums text-slate-500">{formatCurrency(row.saldo_iva)}</td>
                          <td className="text-right tabular-nums font-bold">{formatCurrency(row.saldo_total)}</td>
                          <td>
                            <span className={`px-2 py-1 text-xs font-medium ${row.saldo_base <= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                              {row.estado}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Presupuesto Detalle */}
        <TabsContent value="presupuesto-detalle">
          {loading ? (
            <div className="card-industrial animate-pulse h-96"></div>
          ) : (
            <>
              {/* Export buttons */}
              <div className="flex justify-end gap-2 mb-4">
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={handleExportBudgetDetailPDF}
                  disabled={!budgetDetail}
                >
                  <Download size={16} className="mr-2" />
                  Descargar PDF
                </Button>
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={handleExportBudgetDetailExcel}
                  disabled={!budgetDetail}
                >
                  <FileSpreadsheet size={16} className="mr-2" />
                  Descargar Excel
                </Button>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="card-industrial text-center">
                  <p className="label-overline">Total Anual</p>
                  <p className="text-3xl font-bold">{formatCurrency(budgetDetail?.totals?.total_anual || 0)}</p>
                </div>
                <div className="card-industrial text-center">
                  <p className="label-overline">Total Mensual</p>
                  <p className="text-3xl font-bold">{formatCurrency(budgetDetail?.totals?.total_mensual || 0)}</p>
                </div>
                <div className="card-industrial text-center bg-amber-50">
                  <p className="label-overline">Total Mensual + IVA</p>
                  <p className="text-3xl font-bold text-amber-600">{formatCurrency(budgetDetail?.totals?.total_mensual_con_iva || 0)}</p>
                </div>
              </div>

              {/* Table */}
              <div className="card-industrial overflow-hidden p-0">
                <div className="overflow-x-auto">
                  <table className="table-blueprint">
                    <thead>
                      <tr>
                        <th>Categoría</th>
                        <th>Subcategoría</th>
                        <th className="text-right">Importe Anual</th>
                        <th className="text-right">Importe Mensual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {budgetDetail?.data?.map((cat, catIndex) => (
                        <>
                          {/* Category header */}
                          <tr key={`cat-${catIndex}`} className="bg-slate-100 font-bold">
                            <td colSpan={2}>{cat.categoria}</td>
                            <td className="text-right tabular-nums">{formatCurrency(cat.total_anual)}</td>
                            <td className="text-right tabular-nums">{formatCurrency(cat.total_mensual)}</td>
                          </tr>
                          {/* Subcategories */}
                          {cat.subcategorias?.map((sub, subIndex) => (
                            <tr key={`sub-${catIndex}-${subIndex}`}>
                              <td></td>
                              <td className="pl-6">{sub.subcategoria}</td>
                              <td className="text-right tabular-nums">{formatCurrency(sub.importe_anual)}</td>
                              <td className="text-right tabular-nums">{formatCurrency(sub.importe_mensual)}</td>
                            </tr>
                          ))}
                        </>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-900 text-white font-bold">
                        <td colSpan={2}>TOTAL PRESUPUESTO</td>
                        <td className="text-right tabular-nums">{formatCurrency(budgetDetail?.totals?.total_anual || 0)}</td>
                        <td className="text-right tabular-nums">{formatCurrency(budgetDetail?.totals?.total_mensual || 0)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Cuotas por Lote */}
        <TabsContent value="cuotas-lote">
          {loading ? (
            <div className="card-industrial animate-pulse h-96"></div>
          ) : (
            <>
              {/* Export buttons */}
              <div className="flex justify-end gap-2 mb-4">
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={handleExportFeesPDF}
                  disabled={!feesReport}
                >
                  <Download size={16} className="mr-2" />
                  Descargar PDF
                </Button>
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={handleExportFeesExcel}
                  disabled={!feesReport}
                >
                  <FileSpreadsheet size={16} className="mr-2" />
                  Descargar Excel
                </Button>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="card-industrial text-center">
                  <p className="label-overline">Total Lotes</p>
                  <p className="text-3xl font-bold">{feesReport?.summary?.total_lotes || 0}</p>
                </div>
                <div className="card-industrial text-center">
                  <p className="label-overline">Meses en Período</p>
                  <p className="text-3xl font-bold">{feesReport?.summary?.meses_en_periodo || 0}</p>
                </div>
                <div className="card-industrial text-center">
                  <p className="label-overline">Total Mensual</p>
                  <p className="text-3xl font-bold">{formatCurrency(feesReport?.totals?.total_mensual || 0)}</p>
                </div>
                <div className="card-industrial text-center bg-amber-50">
                  <p className="label-overline">Total Período</p>
                  <p className="text-3xl font-bold text-amber-600">{formatCurrency(feesReport?.totals?.total_periodo || 0)}</p>
                </div>
              </div>

              {/* Table */}
              <div className="card-industrial overflow-hidden p-0">
                <div className="overflow-x-auto">
                  <table className="table-blueprint">
                    <thead>
                      <tr>
                        <SortableHeader label="Lote" sortKey="numero_lote" tableKey="feesLot" />
                        <SortableHeader label="Propietario" sortKey="propietario" tableKey="feesLot" />
                        <SortableHeader label="Superficie" sortKey="metros_cuadrados" tableKey="feesLot" align="right" />
                        <SortableHeader label="Indiviso" sortKey="indiviso" tableKey="feesLot" align="right" />
                        <SortableHeader label="Base/Mes" sortKey="cuota_base_mensual" tableKey="feesLot" align="right" />
                        <SortableHeader label="IVA/Mes" sortKey="cuota_iva_mensual" tableKey="feesLot" align="right" />
                        <SortableHeader label="Total/Mes" sortKey="cuota_total_mensual" tableKey="feesLot" align="right" />
                        <SortableHeader label="Total Período" sortKey="cuota_total_periodo" tableKey="feesLot" align="right" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFeesData?.map((row, index) => (
                        <tr key={index}>
                          <td className="font-mono font-medium">{row.numero_lote}</td>
                          <td>{row.propietario || '-'}</td>
                          <td className="text-right tabular-nums">{row.metros_cuadrados ? `${row.metros_cuadrados} m²` : '-'}</td>
                          <td className="text-right tabular-nums">{row.indiviso}%</td>
                          <td className="text-right tabular-nums">{formatCurrency(row.cuota_base_mensual)}</td>
                          <td className="text-right tabular-nums text-slate-500">{formatCurrency(row.cuota_iva_mensual)}</td>
                          <td className="text-right tabular-nums font-medium">{formatCurrency(row.cuota_total_mensual)}</td>
                          <td className="text-right tabular-nums font-bold text-amber-600">{formatCurrency(row.cuota_total_periodo)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-900 text-white font-bold">
                        <td colSpan={4}>TOTAL</td>
                        <td className="text-right tabular-nums">{formatCurrency(feesReport?.totals?.total_base_mensual || 0)}</td>
                        <td className="text-right tabular-nums">{formatCurrency(feesReport?.totals?.total_iva_mensual || 0)}</td>
                        <td className="text-right tabular-nums">{formatCurrency(feesReport?.totals?.total_mensual || 0)}</td>
                        <td className="text-right tabular-nums">{formatCurrency(feesReport?.totals?.total_periodo || 0)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Gastos Detallado */}
        <TabsContent value="gastos-detalle">
          {expensesDetail && (
            <>
              <div className="card-industrial mb-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <label className="label-overline block mb-2">Categoría</label>
                    <Select value={expCategoriaFilter} onValueChange={(v) => { setExpCategoriaFilter(v); setExpSubcategoriaFilter('all'); }}>
                      <SelectTrigger className="w-52 input-industrial" data-testid="exp-categoria-filter">
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas las categorías</SelectItem>
                        {(expensesDetail?.filters?.categorias || []).map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="label-overline block mb-2">Subcategoría</label>
                    <Select value={expSubcategoriaFilter} onValueChange={setExpSubcategoriaFilter}>
                      <SelectTrigger className="w-52 input-industrial" data-testid="exp-subcategoria-filter">
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas las subcategorías</SelectItem>
                        {expSubcategoriasFiltered.map(sub => (
                          <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 ml-auto">
                    <Button variant="outline" size="sm" onClick={handleExportExpensesPDF} data-testid="exp-export-pdf">
                      <FileText className="w-4 h-4 mr-1" /> PDF
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExportExpensesExcel} data-testid="exp-export-excel">
                      <Download className="w-4 h-4 mr-1" /> Excel
                    </Button>
                  </div>
                </div>
              </div>

              <div className="card-industrial mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="label-overline">Total Gastos</p>
                    <p className="text-2xl font-bold text-red-600" data-testid="exp-total-gastos">
                      {formatCurrency(filteredExpensesTotals.total_gastos)}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="label-overline">Total Registros</p>
                    <p className="text-2xl font-bold text-slate-700" data-testid="exp-total-registros">
                      {filteredExpensesTotals.total_registros}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card-industrial">
                <div className="overflow-x-auto">
                  <table className="table-industrial w-full">
                    <thead>
                      <tr>
                        {[
                          { key: 'fecha', label: 'Fecha' },
                          { key: 'categoria', label: 'Categoría' },
                          { key: 'subcategoria', label: 'Subcategoría' },
                          { key: 'tipo_cuota', label: 'Tipo' },
                          { key: 'proveedor', label: 'Proveedor' },
                          { key: 'descripcion', label: 'Descripción' },
                          { key: 'monto', label: 'Monto' }
                        ].map(col => (
                          <th key={col.key}
                            className="cursor-pointer hover:bg-slate-100 select-none"
                            onClick={() => toggleSort('expensesDetail', col.key)}
                            data-testid={`exp-sort-${col.key}`}
                          >
                            <div className="flex items-center gap-1">
                              {col.label}
                              {sortConfig.expensesDetail.key === col.key && (
                                sortConfig.expensesDetail.direction === 'asc' 
                                  ? <ChevronUp className="w-3 h-3" /> 
                                  : <ChevronDown className="w-3 h-3" />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedExpensesFiltered.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-8 text-slate-400">
                            No hay gastos registrados para este período
                          </td>
                        </tr>
                      ) : (
                        sortedExpensesFiltered.map((row, idx) => (
                          <tr key={`${row.fecha}-${row.descripcion}-${idx}`}>
                            <td className="whitespace-nowrap">{row.fecha}</td>
                            <td>{row.categoria}</td>
                            <td>{row.subcategoria}</td>
                            <td>
                              <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                                row.tipo_cuota === 'extraordinario' 
                                  ? 'bg-amber-100 text-amber-800' 
                                  : 'bg-slate-100 text-slate-600'
                              }`}>
                                {row.tipo_cuota === 'extraordinario' ? 'Extra' : 'Ord.'}
                              </span>
                            </td>
                            <td>{row.proveedor}</td>
                            <td>{row.descripcion}</td>
                            <td className="text-right font-medium text-red-600">{formatCurrency(row.monto)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {sortedExpensesFiltered.length > 0 && (
                      <tfoot>
                        <tr className="bg-slate-100 font-bold">
                          <td colSpan="6" className="text-right">TOTAL</td>
                          <td className="text-right text-red-700">{formatCurrency(filteredExpensesTotals.total_gastos)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Estado de Cuenta */}
        <TabsContent value="estado-cuenta">
          <div className="card-industrial mb-6">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="label-overline block mb-2">Seleccionar Lote</label>
                <Select value={selectedLot} onValueChange={setSelectedLot}>
                  <SelectTrigger className="w-64 input-industrial" data-testid="lot-select">
                    <SelectValue placeholder="Seleccionar lote..." />
                  </SelectTrigger>
                  <SelectContent>
                    {lots.map(lot => (
                      <SelectItem key={lot.id} value={lot.id}>{lot.numero_lote}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {accountStatement ? (
            <>
              {/* Export buttons */}
              <div className="flex justify-end gap-2 mb-4">
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={handleExportAccountPDF}
                  data-testid="export-account-pdf"
                >
                  <Download size={16} className="mr-2" />
                  Descargar PDF
                </Button>
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={handleExportAccountExcel}
                  data-testid="export-account-excel"
                >
                  <FileSpreadsheet size={16} className="mr-2" />
                  Descargar Excel
                </Button>
              </div>

              {/* Lot Info */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="card-industrial">
                  <span className="label-overline">Lote</span>
                  <p className="text-2xl font-black tracking-tight mt-2">{accountStatement.lot.numero_lote}</p>
                  {accountStatement.lot.propietario_nombre && (
                    <p className="text-sm text-slate-500 mt-1">{accountStatement.lot.propietario_nombre}</p>
                  )}
                </div>
                <div className="card-industrial">
                  <span className="label-overline">Total Cargos</span>
                  <p className="text-2xl font-black tracking-tight mt-2 tabular-nums">
                    {formatCurrency(accountStatement.summary.total_cargos_total)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Base: {formatCurrency(accountStatement.summary.total_cargos_base)} + IVA: {formatCurrency(accountStatement.summary.total_cargos_iva)}
                  </p>
                </div>
                <div className="card-industrial">
                  <span className="label-overline">Total Pagos</span>
                  <p className="text-2xl font-black tracking-tight mt-2 tabular-nums text-emerald-600">
                    {formatCurrency(accountStatement.summary.total_abonos_total)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Base: {formatCurrency(accountStatement.summary.total_abonos_base)} + IVA: {formatCurrency(accountStatement.summary.total_abonos_iva)}
                  </p>
                </div>
                <div className="card-industrial">
                  <span className="label-overline">Saldo Actual</span>
                  <p className={`text-2xl font-black tracking-tight mt-2 tabular-nums ${accountStatement.summary.saldo_actual_total > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {formatCurrency(accountStatement.summary.saldo_actual_total)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Base: {formatCurrency(accountStatement.summary.saldo_actual_base)} + IVA: {formatCurrency(accountStatement.summary.saldo_actual_iva)}
                  </p>
                </div>
              </div>

              {/* Movements Table */}
              <div className="card-industrial overflow-hidden p-0">
                <div className="overflow-x-auto">
                  <table className="table-blueprint" data-testid="account-statement-table">
                    <thead>
                      <tr>
                        <SortableHeader label="Fecha" sortKey="fecha" tableKey="accountStatement" />
                        <SortableHeader label="Concepto" sortKey="concepto" tableKey="accountStatement" />
                        <SortableHeader label="Base" sortKey="cargo_base" tableKey="accountStatement" align="right" />
                        <SortableHeader label="IVA (16%)" sortKey="cargo_iva" tableKey="accountStatement" align="right" />
                        <SortableHeader label="Total" sortKey="cargo_total" tableKey="accountStatement" align="right" />
                        <SortableHeader label="Saldo" sortKey="saldo_total" tableKey="accountStatement" align="right" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAccountMovements?.map((mov, index) => {
                        const isCharge = mov.cargo_total > 0;
                        const baseAmount = isCharge ? mov.cargo_base : mov.abono_base;
                        const ivaAmount = isCharge ? mov.cargo_iva : mov.abono_iva;
                        const totalAmount = isCharge ? mov.cargo_total : mov.abono_total;
                        const colorClass = isCharge ? 'text-red-600' : 'text-emerald-600';
                        const prefix = isCharge ? '' : '-';
                        
                        return (
                          <tr key={index}>
                            <td className="font-mono text-sm">{mov.fecha}</td>
                            <td>{mov.concepto}</td>
                            <td className={`text-right tabular-nums ${colorClass}`}>
                              {baseAmount > 0 ? `${prefix}${formatCurrency(baseAmount)}` : '-'}
                            </td>
                            <td className={`text-right tabular-nums ${colorClass}`}>
                              {ivaAmount > 0 ? `${prefix}${formatCurrency(ivaAmount)}` : '-'}
                            </td>
                            <td className={`text-right tabular-nums font-medium ${colorClass}`}>
                              {totalAmount > 0 ? `${prefix}${formatCurrency(totalAmount)}` : '-'}
                            </td>
                            <td className={`text-right tabular-nums font-medium ${mov.saldo_total > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                              {formatCurrency(mov.saldo_total)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state py-16 card-industrial">
              <div className="empty-state-icon">
                <FileText size={24} className="text-slate-400" />
              </div>
              <h3>Selecciona un lote</h3>
              <p>Elige un lote para ver su estado de cuenta.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reportes;
